<template>
  <div :class="containerClass">
    <!-- นาฬิกาแบบเต็ม -->
    <div v-if="type === 'full'" class="flex flex-col items-center">
      <div class="text-2xl font-bold text-gray-900">
        {{ currentTime24WithSeconds }}
      </div>
      <div class="text-sm text-gray-600 mt-1">
        {{ currentThaiDateWithDay }}
      </div>
    </div>

    <!-- นาฬิกาแบบกะทัดรัด -->
    <div v-else-if="type === 'compact'" class="flex items-center space-x-2">
      <div class="text-lg font-semibold text-gray-900">
        {{ currentTime24 }}
      </div>
      <div class="text-sm text-gray-600">
        {{ currentThaiDateShort }}
      </div>
    </div>

    <!-- นาฬิกาแบบเฉพาะเวลา -->
    <div v-else-if="type === 'time-only'" class="text-xl font-bold text-gray-900">
      {{ showSeconds ? currentTime24WithSeconds : currentTime24 }}
    </div>

    <!-- นาฬิกาแบบเฉพาะวันที่ -->
    <div v-else-if="type === 'date-only'" class="text-lg text-gray-900">
      {{ currentThaiDate }}
    </div>

    <!-- นาฬิกาแบบ header -->
    <div v-else-if="type === 'header'" class="flex items-center space-x-4">
      <div class="text-lg font-semibold text-gray-900">
        {{ showSeconds ? currentTime24WithSeconds : currentTime24 }}
      </div>
      <div class="text-sm text-gray-600">
        {{ currentThaiDateWithDay }}
      </div>
    </div>

    <!-- นาฬิกาแบบ digital -->
    <div v-else-if="type === 'digital'" class="bg-gray-900 text-green-400 p-4 rounded-lg font-mono">
      <div class="text-3xl font-bold text-center">
        {{ currentTime24WithSeconds }}
      </div>
      <div class="text-center text-sm mt-2 text-green-300">
        {{ currentThaiDateWithDay }}
      </div>
    </div>

    <!-- นาฬิกาแบบ card -->
    <div v-else-if="type === 'card'" class="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
      <div class="flex items-center justify-between">
        <div>
          <div class="text-2xl font-bold text-gray-900">
            {{ currentTime24 }}
          </div>
          <div class="text-sm text-gray-600 mt-1">
            {{ currentThaiDate }}
          </div>
        </div>
        <div class="text-right">
          <div class="text-sm text-gray-500">
            {{ getThaiDayName(currentTime.getDay()) }}
          </div>
          <div class="text-xs text-gray-400 mt-1">
            พ.ศ. {{ currentBuddhistYear }}
          </div>
        </div>
      </div>
    </div>

    <!-- นาฬิกาแบบ minimal -->
    <div v-else class="text-sm text-gray-600">
      {{ currentThaiDateTime }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { useDateTime } from '../composables/useDateTime'

interface Props {
  /** ประเภทของนาฬิกา */
  type?: 'full' | 'compact' | 'time-only' | 'date-only' | 'header' | 'digital' | 'card' | 'minimal'
  /** แสดงวินาทีหรือไม่ */
  showSeconds?: boolean
  /** แสดงปี พ.ศ. หรือไม่ */
  showBuddhistYear?: boolean
  /** CSS class สำหรับ container */
  containerClass?: string
}

const props = withDefaults(defineProps<Props>(), {
  type: 'full',
  showSeconds: true,
  showBuddhistYear: false,
  containerClass: ''
})

const {
  currentTime,
  currentThaiDate,
  currentThaiDateShort,
  currentThaiDateTime,
  currentTime24,
  currentTime24WithSeconds,
  currentThaiDateWithDay,
  currentBuddhistYear,
  getThaiDayName
} = useDateTime()
</script>

<style scoped>
/* Digital clock styling */
.font-mono {
  font-family: 'Courier New', Courier, monospace;
}
</style>